import React, { useMemo, useRef } from "react";
import { toast } from "react-toastify";
import AddGroupMutation from "../../components/stages/mutation/AddGroup";
import { Form } from "react-router-dom";
import { GlobalStyles } from "../../constants/styles";
import useTranslation from "../../i18n/useTranslation";

const CreateGroup = ({ groups, setGroups, stageId, scheduleType }) => {
    const { t } = useTranslation();
    const [addGroup] = AddGroupMutation();
    const groupNameInput = useRef(null);

    const ui = useMemo(() => {
        return {
            wrap: {
                borderRadius: 16,
                padding: 12,
                border: "1px solid rgba(0,0,0,0.08)",
                background: "rgba(0,0,0,0.012)",
            },
            row: {
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: 10,
                alignItems: "center",
            },
            label: {
                display: "block",
                fontWeight: 900,
                fontSize: 12.5,
                color: "rgba(20,21,26,0.72)",
                marginBottom: 8,
                letterSpacing: "0.02em",
                textTransform: "uppercase",
            },
            input: {
                borderRadius: 14,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                padding: "12px 14px",
                fontWeight: 900,
                fontSize: 14.5,
                color: "#14151a",
                width: "100%",
            },
            btn: {
                borderRadius: 14,
                padding: "12px 14px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                border: "1px solid rgba(0,0,0,0.10)",
                background: GlobalStyles.colors.canary,
                color: GlobalStyles.colors.woodsmoke,
                cursor: "pointer",
                whiteSpace: "nowrap",
                lineHeight: 1,
            },
            hint: {
                marginTop: 8,
                color: "rgba(20,21,26,0.55)",
                fontWeight: 700,
                fontSize: 12.5,
            },
            srOnly: {
                position: "absolute",
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: "hidden",
                clip: "rect(0, 0, 0, 0)",
                whiteSpace: "nowrap",
                border: 0,
            },
        };
    }, []);

    const getLabels = () => {
        const normalizedType = String(scheduleType || "").toUpperCase();

        if (normalizedType === "ROUND_ROBIN" || normalizedType === "LEAGUE") {
            return {
                noun: t("createGroup.nouns.group"),
                placeholder: t("createGroup.placeholders.groupName"),
                cta: t("createGroup.buttons.createGroup"),
                label: t("createGroup.labels.groupName"),
                hintItemName: t("createGroup.nouns.group").toLowerCase() + "s",
                icon: "fa-layer-group",
            };
        }

        if (normalizedType === "TRAINING") {
            return {
                noun: t("createGroup.nouns.pool"),
                placeholder: t("createGroup.placeholders.poolName"),
                cta: t("createGroup.buttons.createPool"),
                label: t("createGroup.labels.poolName"),
                hintItemName: t("createGroup.nouns.pool").toLowerCase() + "s",
                icon: "fa-dumbbell",
            };
        }

        return {
            noun: t("createGroup.nouns.game"),
            placeholder: t("createGroup.placeholders.gameName"),
            cta: t("createGroup.buttons.createGame"),
            label: t("createGroup.labels.gameName"),
            hintItemName: t("createGroup.nouns.game").toLowerCase() + "s",
            icon: "fa-futbol",
        };
    };

    const { noun, placeholder, cta, label, hintItemName, icon } = getLabels();

    const refreshGroups = (result) => {
        if (result.data?.createGroup) {
            const groupList = [
                ...(groups || []),
                {
                    id: result.data.createGroup.group.id,
                    name: result.data.createGroup.group.name,
                    teams: [],
                },
            ];

            setGroups(groupList);

            if (groupNameInput.current) {
                groupNameInput.current.value = "";
            }

            toast.success(
                t("createGroup.toasts.createdSuccessfully", { noun }),
                {
                    autoClose: 3000,
                    toastId: "group_added",
                    position: "top-center",
                    closeOnClick: true,
                }
            );
        }
    };

    const disabledSubmit = scheduleType === "TRAINING" && groups.length > 0;

    const handleSubmit = (e) => {
        e.preventDefault();

        const name = groupNameInput.current?.value?.trim();
        if (!name) return;

        if (disabledSubmit) {
            toast.error(t("createGroup.toasts.onlyOnePoolAllowed"), {
                autoClose: 3000,
                toastId: "group_add_error",
                position: "top-center",
                closeOnClick: true,
            });
            return;
        }

        addGroup({
            variables: {
                name,
                stageId: parseInt(stageId, 10),
            },
        })
            .then((result) => refreshGroups(result))
            .catch((err) => {
                toast.error(
                    err?.message ||
                    t("createGroup.toasts.failedToCreate", {
                        noun: noun.toLowerCase(),
                    }),
                    {
                        autoClose: 3000,
                        toastId: "group_add_error",
                        position: "top-center",
                        closeOnClick: true,
                    }
                );
            });
    };

    return (
        <Form method="post" onSubmit={handleSubmit} style={ui.wrap} id="kbola-create-group">
            <label htmlFor="kbola-group-name" style={ui.label}>
                {label}
            </label>

            <div style={ui.row} className="kbola-createGroupRow">
                <input
                    id="kbola-group-name"
                    type="text"
                    ref={groupNameInput}
                    placeholder={placeholder}
                    style={{
                        ...ui.input,
                        ...(disabledSubmit
                            ? {
                                backgroundColor: GlobalStyles.colors.manatee,
                                color: GlobalStyles.colors.white,
                                cursor: "not-allowed",
                                opacity: 0.8,
                            }
                            : {}),
                    }}
                    className="kbola-createGroupInput"
                    autoComplete="off"
                    disabled={disabledSubmit}
                />

                <button
                    type="submit"
                    className="kbola-createGroupBtn"
                    disabled={disabledSubmit}
                    style={{
                        ...ui.btn,
                        ...(disabledSubmit
                            ? {
                                backgroundColor: GlobalStyles.colors.manatee,
                                cursor: "not-allowed",
                                opacity: 0.8,
                            }
                            : {}),
                    }}
                >
                    <i className={`fas ${icon}`} />
                    {cta}
                </button>
            </div>

            <div style={ui.hint}>
                {t("createGroup.hints.tip", {
                    itemName: hintItemName,
                    randomDraft: t("createGroup.hints.randomDraft"),
                })}
            </div>

            <style>{`
                #kbola-create-group .kbola-createGroupInput:focus{
                  outline: none !important;
                  box-shadow: 0 0 0 0.22rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.22) !important;
                  border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.75) !important;
                }

                #kbola-create-group button:focus{
                  outline: none;
                  box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
                }

                @media (max-width: 640px){
                  #kbola-create-group .kbola-createGroupRow{
                    grid-template-columns: 1fr !important;
                  }

                  #kbola-create-group .kbola-createGroupBtn{
                    justify-content: center;
                    width: 100%;
                  }
                }
            `}</style>
        </Form>
    );
};

export default CreateGroup;